import * as Sentry from '@sentry/browser';

import * as userActions from 'dux/user/actions';
import * as authActions from 'dux/auth/actions';
import * as AuthService from 'Services/AuthService';
import * as CouponService from 'Services/CouponService';
import * as trackingActions from 'dux/tracking/actions';
import {
  isExistingUser,
  isFetchError,
  isInvalidCoupon,
  isValidationError,
} from 'Services/HTTPError';
import { RETURNING_CONSULTATION_ROUTE } from 'Apps/Consultation/constants';
import { getUserReferralCode } from 'dux/user/selectors';

import {
  SIGNUP_COUPON_FAILURE,
  SIGNUP_FAILURE,
  SIGNUP_MAGICLINK_FAILURE,
  SIGNUP_MAGICLINK_SUCCESS,
  SIGNUP_REQUEST,
  SIGNUP_SUCCESS,
  SIGNUP_VALIDATION_FAILURE,
} from './actionTypes';

const signupFields = ['email', 'username', 'first_name', 'last_name'];

const getMagicLinkParams = ({ user, referralCode }) => {
  const params = {
    next: RETURNING_CONSULTATION_ROUTE,
    username: user.username,
  };

  if (referralCode) {
    params.coupon = referralCode;
  }

  return params;
};

// Submits user for signup
//
// actions: optional actions formik object to define field error
export const signupUser = (user, actions, extraMagicLinkParams = {}) => async (
  dispatch,
  getState
) => {
  const referralCode = getUserReferralCode(getState());
  try {
    const { first_name, last_name, email } = user;
    Sentry.addBreadcrumb({
      message: `Signup ${first_name} ${last_name} ${email}`,
      category: 'view',
    });

    dispatch({ type: SIGNUP_REQUEST });

    await AuthService.signup(user);

    await dispatch(userActions.fetchUser());

    if (referralCode) {
      await CouponService.create(referralCode);
    }

    dispatch(authActions.signupSuccess());
    dispatch(trackingActions.trackSignup({ first_name, last_name, email }));

    dispatch({ type: SIGNUP_SUCCESS });
    actions?.onSuccess?.();

    // hack: returns success to parent
    return true;
  } catch (error) {
    if (isExistingUser(error)) {
      const params = {
        ...getMagicLinkParams({ user, referralCode }),
        ...extraMagicLinkParams,
      };

      const result = await AuthService.requestMagicLink(params);

      if (result === 'success') {
        dispatch({ type: SIGNUP_MAGICLINK_SUCCESS, firstname: user.first_name });
      } else {
        dispatch({ type: SIGNUP_MAGICLINK_FAILURE });
      }
      actions?.setSubmitting(false);
    } else if (
      actions && // hack: check if actions are available because gifting does not support this
      isValidationError(error) &&
      signupFields.some(field => error.extra[field])
    ) {
      signupFields.forEach(
        field => error.extra[field] && actions.setFieldError(field, error.extra[field].join(' '))
      );
      actions?.setSubmitting(false);

      dispatch({ type: SIGNUP_VALIDATION_FAILURE });
    } else if (isInvalidCoupon(error)) {
      dispatch({ type: SIGNUP_COUPON_FAILURE });
    } else {
      if (!isFetchError(error)) {
        Sentry.captureMessage('API Service (AuthService.signup)');
      }

      dispatch({ type: SIGNUP_FAILURE });
      actions?.setSubmitting(false);
    }

    // hack: returns failure to parent
    return false;
  }
};
